import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Menu, X, MessageCircle } from "lucide-react";

export const WHATSAPP_LINK = "https://wa.me/27721874531?text=Hello%20Infinium%20Innovations";
export const LOGO_WHITE = "https://customer-assets.emergentagent.com/job_animated-infinium/artifacts/orll1opm_INFINIUM%20LOGO-infinity.png";
export const LOGO_BLACK = "https://customer-assets.emergentagent.com/job_animated-infinium/artifacts/orll1opm_INFINIUM%20LOGO-infinity.png";
export const HERO_BG = "https://images.unsplash.com/photo-1679128991312-2681b085903e?auto=format&fit=crop&w=1920&q=80";
export const TEAM_IMG = "https://images.unsplash.com/photo-1573879410138-96c2a1ad3210?auto=format&fit=crop&w=800&q=80";

export function WhatsAppButton() {
  return (
    <a
      href={WHATSAPP_LINK}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-button"
      className="fixed bottom-8 right-8 z-50 bg-green-500 text-white p-4 rounded-full shadow-xl hover:scale-110 transition-transform duration-300 flex items-center justify-center whatsapp-pulse"
    >
      <MessageCircle size={28} fill="white" strokeWidth={0} />
    </a>
  );
}

export function Navigation({ isScrolled, forceDark }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  const showDark = forceDark || isScrolled || !isHomePage;

  const navBg = showDark ? "bg-white/95 backdrop-blur-md shadow-lg" : "bg-transparent";
  const textColor = showDark ? "text-slate-700 hover:text-cyan-600" : "text-white/90 hover:text-white";
  const iconColor = showDark ? "text-slate-900" : "text-white";
  const logoSrc = showDark ? LOGO_BLACK : LOGO_WHITE;

  return (
    <nav data-testid="navigation" className={"fixed top-0 left-0 right-0 z-40 transition-all duration-300 " + navBg}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="logo">
            <img src={logoSrc} alt="Infinium Innovations" className="h-10 w-auto" />
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className={textColor + " nav-link font-medium transition-colors"}>Home</Link>
            <Link to="/about" className={textColor + " nav-link font-medium transition-colors"}>About</Link>
            <Link to="/services" className={textColor + " nav-link font-medium transition-colors"}>Services</Link>
            <Link to="/contact" className={textColor + " nav-link font-medium transition-colors"}>Contact</Link>
            <Link to="/contact">
              <Button className="btn-gradient text-white rounded-full px-6 py-2 font-medium" data-testid="nav-cta">
                Get in Touch
              </Button>
            </Link>
          </div>

          <button data-testid="mobile-menu-btn" className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className={iconColor} size={24} /> : <Menu className={iconColor} size={24} />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden absolute top-20 left-0 right-0 bg-white shadow-lg py-6">
            <Link to="/" onClick={() => setMobileMenuOpen(false)} className="block px-6 py-3 text-slate-700 hover:text-cyan-600 font-medium">Home</Link>
            <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="block px-6 py-3 text-slate-700 hover:text-cyan-600 font-medium">About</Link>
            <Link to="/services" onClick={() => setMobileMenuOpen(false)} className="block px-6 py-3 text-slate-700 hover:text-cyan-600 font-medium">Services</Link>
            <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className="block px-6 py-3 text-slate-700 hover:text-cyan-600 font-medium">Contact</Link>
          </div>
        )}
      </div>
    </nav>
  );
}

export function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="footer" className="bg-slate-950 border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex flex-col items-center md:items-start">
            <img src={LOGO_WHITE} alt="Infinium Innovations" className="h-10 w-auto mb-3" />
            <p className="text-slate-400 text-sm text-center md:text-left">Strategy & Innovation Consultancy</p>
          </div>

          <div className="flex gap-8">
            <Link to="/" className="text-slate-400 hover:text-white text-sm transition-colors">Home</Link>
            <Link to="/about" className="text-slate-400 hover:text-white text-sm transition-colors">About</Link>
            <Link to="/services" className="text-slate-400 hover:text-white text-sm transition-colors">Services</Link>
            <Link to="/contact" className="text-slate-400 hover:text-white text-sm transition-colors">Contact</Link>
          </div>
        </div>

        <Separator className="my-8 bg-slate-800" />

        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-slate-500">
          <p>© {currentYear} Infinium Innovations. All rights reserved.</p>
          <p>Working where complexity meets execution.</p>
        </div>
      </div>
    </footer>
  );
}

export function PageHeader({ title, subtitle, badge }) {
  return (
    <section className="relative pt-32 pb-20 bg-slate-950 overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: "radial-gradient(circle at 20% 50%, rgba(6, 182, 212, 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(59, 130, 246, 0.3) 0%, transparent 50%)"
        }} />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
        {badge && (
          <span className="inline-block px-4 py-2 rounded-full text-sm font-medium bg-white/10 text-cyan-400 backdrop-blur-sm mb-6">
            {badge}
          </span>
        )}
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">{title}</h1>
        {subtitle && <p className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto">{subtitle}</p>}
      </div>
    </section>
  );
}

export function CTASection() {
  return (
    <section className="py-24 md:py-32 bg-slate-950 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: "radial-gradient(circle at 20% 50%, rgba(6, 182, 212, 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(59, 130, 246, 0.3) 0%, transparent 50%)"
        }} />
      </div>
      <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
          Let's work through the<br /><span className="text-gradient">complexity together</span>
        </h2>
        <p className="text-slate-400 text-lg mb-10 max-w-2xl mx-auto">
          Ready to navigate complexity and drive transformative growth? Start a conversation with our team today.
        </p>
        <a
          href={WHATSAPP_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-3 btn-gradient text-white rounded-full px-8 py-4 text-lg font-medium hover:scale-105 transition-transform"
        >
          <MessageCircle size={24} />
          Start a Conversation
        </a>
      </div>
    </section>
  );
}
